import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserCheckIcon } from "lucide-react";
import { authorizedFetch } from "@/lib/api";
import { API_URL } from "@/lib/mutation-utils";

interface Driver {
  id: string;
  code: string;
  name: string;
}

interface Props {
  driver: Driver | null;
  onClose: () => void;
}

export function CreateDriverUserSheet({ driver, onClose }: Props) {
  const queryClient = useQueryClient();

  const suggestedEmail = driver
    ? `${driver.code.toLowerCase().replace(/[^a-z0-9]/g, "")}@cdc.internal`
    : "";

  const [email, setEmail]       = useState(suggestedEmail);
  const [password, setPassword] = useState("");
  const [error, setError]       = useState<string | null>(null);

  function handleOpenChange(open: boolean) {
    if (!open) {
      setEmail("");
      setPassword("");
      setError(null);
      onClose();
    }
  }

  if (driver && email === "") {
    setEmail(suggestedEmail);
  }

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await authorizedFetch(
        `${API_URL}/api/providers/drivers/${driver!.id}/create-user`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, password }),
        }
      );
      if (!res.ok) {
        const body = await res.json() as { error?: string };
        throw new Error(body.error ?? "Error al crear usuario");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["drivers"] });
      handleOpenChange(false);
    },
    onError: (err: Error) => setError(err.message),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    mutation.mutate();
  }

  return (
    <Sheet open={!!driver} onOpenChange={handleOpenChange}>
      <SheetContent side="right" className="sm:max-w-sm">
        <SheetHeader>
          <SheetTitle>Crear acceso de chofer</SheetTitle>
          <SheetDescription>
            Se creará un usuario con rol <strong>Chofer</strong> para{" "}
            <strong>{driver?.name}</strong>.
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="mt-4 flex flex-col gap-4 px-6 pb-6">
          <div className="rounded-md border bg-muted/40 px-4 py-3 text-sm space-y-1">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Código</span>
              <span className="font-mono font-medium">{driver?.code}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Nombre</span>
              <span className="font-medium">{driver?.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Rol</span>
              <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 dark:text-blue-400">
                <UserCheckIcon className="size-3" /> Chofer
              </span>
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="cdu-email">
              Email <span className="text-destructive">*</span>
            </Label>
            <Input
              id="cdu-email"
              type="email"
              placeholder="correo@ejemplo.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="off"
            />
            <p className="text-[11px] text-muted-foreground">
              Puede ser una dirección interna (ej. código@cdc.internal).
            </p>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="cdu-password">
              Contraseña <span className="text-destructive">*</span>
            </Label>
            <Input
              id="cdu-password"
              type="password"
              placeholder="mínimo 8 caracteres"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
            />
          </div>

          {error && (
            <p className="text-sm text-destructive text-center">{error}</p>
          )}

          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={() => handleOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="flex-1"
              disabled={mutation.isPending}
            >
              {mutation.isPending ? "Creando..." : "Crear usuario"}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
